import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Alert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { PictureAsPdf, Print } from "@mui/icons-material";
import IncomeStatement from "./IncomeStatement";
import useAuth from "../../../hooks/useAuth";
import {
  fetchFinancialStatementsWithPrev,
  fetchFiscalYears,
} from "../../../redux/slices/accounting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import axiosInstance from "../../../utils/axios";
import { format } from "date-fns";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const PrintableContainer = styled("div")`
  .print-only {
    display: none;
  }

  @media print {
    & .no-print,
    & .MuiBreadcrumbs-root,
    & .MuiDivider-root {
      display: none !important;
    }

    .print-only {
      display: block !important;
    }

    /* Generic table print styles (apply to tables inside IncomeStatement) */
    & .MuiTableCell-root {
      border: 1px solid #000 !important;
      font-size: 10px !important;
      padding: 4px !important;
    }
    & .MuiTableHead-root .MuiTableCell-root {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }
  }
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function FinancialStatement() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    fiscalYears,
    incomeStatement,
    assets,
    liabilities,
    incomeStatementPrev,
    assetsPrev,
    liabilitiesPrev,
    status,
  } = useAppSelector((state) => state.accounting);
  const isReady = status === "idle" && incomeStatement && assets && liabilities;

  // The page loads the fiscal years itself: activeFiscalYear in the store is
  // only set when Etusivu has been visited first, so a reload or a bookmark
  // used to leave the page empty and the PDF button disabled.
  useEffect(() => {
    if (associationId) dispatch(fetchFiscalYears(associationId));
  }, [dispatch, associationId]);

  const [fiscalYearId, setFiscalYearId] = useState<number | "">("");
  const sortedFiscalYears = [...fiscalYears].sort(
    (a, b) => new Date(b.from).getTime() - new Date(a.from).getTime()
  );
  useEffect(() => {
    if (fiscalYearId !== "" || sortedFiscalYears.length === 0) return;
    const current =
      sortedFiscalYears.find((fy) => fy.active) ?? sortedFiscalYears[0];
    if (current.id) setFiscalYearId(current.id);
  }, [sortedFiscalYears, fiscalYearId]);
  const fiscalYear = fiscalYears.find((fy) => fy.id === fiscalYearId);

  const periodLabel = (fy: { from: Date | string; to: Date | string }) =>
    `${format(new Date(fy.from), "d.M.yyyy")} – ${format(
      new Date(fy.to),
      "d.M.yyyy"
    )}`;

  const [value, setValue] = React.useState(0);
  const tabLabels = ["Tuloslaskelma", "Tase vastaavaa", "Tase vastattavaa"];

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = async () => {
    if (!fiscalYear) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const { data } = await axiosInstance.get(
        "/accounting/reports/financial-statement/pdf",
        {
          params: { associationId, fiscalYearId: fiscalYear.id },
          responseType: "blob",
        }
      );
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tilinpaatos-${format(
        new Date(fiscalYear.from),
        "yyyy-MM-dd"
      )}_${format(new Date(fiscalYear.to), "yyyy-MM-dd")}.pdf`;
      link.click();
      // Some browsers read the blob only after click() returns, so revoking
      // at once can cancel the download.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      // With responseType "blob" the server's JSON error arrives as a Blob too.
      let message: string | undefined;
      try {
        message = JSON.parse(await error?.response?.data?.text())?.message;
      } catch {
        message = undefined;
      }
      setPdfError(message ?? "Tilinpäätöksen PDF:n luominen epäonnistui.");
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser || !fiscalYear) return;
    dispatch(
      fetchFinancialStatementsWithPrev({
        associationId,
        from: fiscalYear.from,
        to: fiscalYear.to,
      })
    );
  }, [dispatch, currentUser, fiscalYear, associationId]);

  return (
    <PrintableContainer>
      <Helmet title="Blank" />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        className="no-print"
      >
        <Typography variant="h3" gutterBottom display="inline">
          Tulos & Tase
        </Typography>
      </Box>

      <Typography variant="h5" className="print-only" gutterBottom>
        Tulos & Tase — {tabLabels[value]}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} className="no-print">
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tulos & Tase</Typography>
      </Breadcrumbs>

      <Divider my={6} className="no-print" />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card mb={6}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Tulos & Tase
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    select
                    size="small"
                    label="Tilikausi"
                    value={fiscalYearId}
                    onChange={(event) =>
                      setFiscalYearId(Number(event.target.value))
                    }
                    sx={{ minWidth: 220 }}
                    className="no-print"
                  >
                    {sortedFiscalYears.map((fy) => (
                      <MenuItem key={fy.id} value={fy.id}>
                        {periodLabel(fy)}
                        {fy.active ? " (nykyinen)" : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    startIcon={
                      pdfLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PictureAsPdf />
                      )
                    }
                    onClick={handleDownloadPdf}
                    disabled={!fiscalYear || pdfLoading}
                    className="no-print"
                  >
                    Lataa tilinpäätös (PDF)
                  </Button>
                  <IconButton
                    onClick={handlePrint}
                    title="Tulosta"
                    className="no-print"
                  >
                    <Print />
                  </IconButton>
                </Box>
              </Box>

              {pdfError && (
                <Alert
                  severity="error"
                  onClose={() => setPdfError(null)}
                  sx={{ mb: 2 }}
                  className="no-print"
                >
                  {pdfError}
                </Alert>
              )}

              <Paper>
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                    className="no-print"
                  >
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="financial statements tabs"
                    >
                      <Tab label="Tuloslaskelma" {...a11yProps(0)} />
                      <Tab label="Tase vastaavaa" {...a11yProps(1)} />
                      <Tab label="Tase vastattavaa" {...a11yProps(2)} />
                    </Tabs>
                  </Box>

                  {!isReady ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="50vh"
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <CustomTabPanel value={value} index={0}>
                        <IncomeStatement
                          rows={incomeStatement}
                          prevRows={incomeStatementPrev}
                        />
                      </CustomTabPanel>

                      <CustomTabPanel value={value} index={1}>
                        <IncomeStatement rows={assets} prevRows={assetsPrev} />
                      </CustomTabPanel>

                      <CustomTabPanel value={value} index={2}>
                        <IncomeStatement
                          rows={liabilities}
                          prevRows={liabilitiesPrev}
                        />
                      </CustomTabPanel>
                    </>
                  )}
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PrintableContainer>
  );
}

export default FinancialStatement;
